import { ChatThreadModel, ThreadApi } from "../../../@api";

import { IChatThreadExtendedModel } from "../../models/interfaces";
import { IStoreContext } from "../../stores/StoreContext";
import { THREAD_PAGE_SIZE } from "../../stores/ThreadStore";
import { ThreadApiService } from "../ApiServices/ThreadApiService";
import GenericService from "../GenericService";

export class ThreadService extends GenericService<ThreadApi, ThreadApiService> {
  constructor(storeContext: IStoreContext) {
    super(storeContext, ThreadApi, ThreadApiService);
  }

  public async openThread(thread: IChatThreadExtendedModel): Promise<void> {
    const { threadStore } = this.storeContext;
    const opening = threadStore.beginOpeningThread(thread);

    // Skip only when the same thread is already loading to avoid duplicate in-flight requests
    if (!opening.shouldLoadFromApi) {
      return;
    }

    try {
      const threadDetails = await this.apiService.getThreadById({ threadId: thread.id });

      threadStore.completeActiveThreadLoad(opening.requestId, {
        ...threadDetails,
        pendingPrompt: thread.pendingPrompt,
        loadState: thread.loadState,
        activeThreadLoadRequestId: thread.activeThreadLoadRequestId,
      });
    } catch (error) {
      threadStore.failActiveThreadLoad(opening.requestId, thread.id);
      throw error;
    }
  }

  public async retryActiveThreadLoad(): Promise<void> {
    const { threadStore } = this.storeContext;
    const activeThread = threadStore.activeThread;

    if (!threadStore.hasActiveThreadLoadError || !activeThread?.id) {
      return;
    }

    await this.openThread(activeThread);
  }

  public async loadNextThreads(): Promise<void> {
    const { threadStore } = this.storeContext;

    if (!threadStore.hasMoreThreads) {
      return;
    }

    await this.loadThreadsPage(threadStore.nextThreadPageIndex);
  }

  public async retryFailedThreadPage(): Promise<void> {
    const { failedThreadPageIndex } = this.storeContext.threadStore;

    if (!failedThreadPageIndex) {
      return;
    }

    await this.loadThreadsPage(failedThreadPageIndex);
  }

  public async refreshThreadPagesAfterUpdate(threadId: string): Promise<void> {
    const { threadStore } = this.storeContext;
    const updatedThreadPageIndex = threadStore.getThreadPageIndex(threadId);

    // New thread: reloads all already loaded pages
    // Existing thread: reloads pages from 0 to the current thread page
    const pageIndexesToReload =
      updatedThreadPageIndex === undefined
        ? threadStore.loadedThreadPageIndexes
        : Array.from({ length: updatedThreadPageIndex + 1 }, (_, pageIndex) => pageIndex);

    await this.reloadThreadsPages(pageIndexesToReload);
  }

  public async deleteThreadById(threadId: string): Promise<void> {
    const { threadStore } = this.storeContext;
    const deletedThreadPageIndex = threadStore.getThreadPageIndex(threadId) ?? 0;
    const loadedPageIndexesBeforeDelete = threadStore.loadedThreadPageIndexes;

    await this.apiService.deleteThreadById({ threadId });
    threadStore.removeThread(threadId);

    if (!loadedPageIndexesBeforeDelete.length) {
      return;
    }

    const pageIndexesToReload = loadedPageIndexesBeforeDelete.filter(pageIndex => pageIndex >= deletedThreadPageIndex);
    await this.reloadThreadsPages(pageIndexesToReload.length ? pageIndexesToReload : [deletedThreadPageIndex]);
  }

  public async loadThreadsPage(pageIndex: number, forceReload = false): Promise<void> {
    const { threadStore } = this.storeContext;

    if (!threadStore.startLoadingThreadPage(pageIndex, forceReload)) {
      return;
    }

    try {
      const pagedThreads = await this.apiService.getAllUserThreads({
        pageIndex,
        pageSize: THREAD_PAGE_SIZE,
      });

      const existingThreads = threadStore.threads;
      const updatedThreads = pagedThreads.entities.map((updatedThread: ChatThreadModel) => {
        const existingThread = existingThreads.find(
          (thread: IChatThreadExtendedModel) => thread.id === updatedThread.id,
        );

        return {
          ...updatedThread,
          pendingPrompt: existingThread?.pendingPrompt,
          loadState: existingThread?.loadState || "loading",
          activeThreadLoadRequestId: existingThread?.activeThreadLoadRequestId || 0,
        };
      });

      threadStore.mergeThreadPage(pageIndex, updatedThreads, pagedThreads.totalCount);
    } catch {
      threadStore.setThreadLoadError(pageIndex);
    } finally {
      threadStore.finishLoadingThreadPage(pageIndex);
    }
  }

  // Reloads the specified thread pages, ensuring they are fetched from the server even if they were previously loaded
  private async reloadThreadsPages(pageIndexes: number[]): Promise<void> {
    const uniqueSortedPageIndexes = Array.from(new Set(pageIndexes.filter(pageIndex => pageIndex >= 0))).sort(
      (a, b) => a - b,
    );

    for (const pageIndex of uniqueSortedPageIndexes) {
      await this.loadThreadsPage(pageIndex, true);
    }
  }
}
